import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Divider,
  Checkbox,
  useTheme,
  Collapse,
  IconButton,
  CircularProgress,
  Button,
} from "@mui/material";
import FilterIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/Filter.js";
import ExpandDownIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandDown.js";
import ExpandUpIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandUp.js";
import ClearIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/Clear.js";
import type { FilterValuesResponse } from "../services/filterValuesService";
import { DesktopDatePicker } from "@mui/x-date-pickers-pro";

interface FilterPanelProps {
  filterValues?: FilterValuesResponse | null;
  onPolicyCodeChange?: (filters: Record<string, boolean>) => void;
  onLanguageChange?: (filters: Record<string, boolean>) => void;
  onDocumentTypeChange?: (filters: Record<string, boolean>) => void;
  onDocumentCategoryChange?: (filters: Record<string, boolean>) => void;
  onDocumentSubCategoryChange?: (filters: Record<string, boolean>) => void;
  onPublishedSinceChange?: (date: Date | null) => void;
  isLoading?: boolean;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filterValues,
  onPolicyCodeChange,
  onLanguageChange,
  onDocumentTypeChange,
  onDocumentCategoryChange,
  onDocumentSubCategoryChange,
  onPublishedSinceChange,
  isLoading,
}) => {
  const theme = useTheme();

  // Helper function to create filter state from API data
  const createFilterState = (
    filterOptions: Array<Record<string, number>> = []
  ) => {
    const state: Record<string, boolean> = {};
    filterOptions.forEach((option) => {
      Object.keys(option).forEach((key) => {
        state[key] = false;
      });
    });
    return state;
  };

  // Filter states - dynamic based on API data
  const [language, setLanguage] = useState<Record<string, boolean>>({});
  const [policyCode, setPolicyCode] = useState<Record<string, boolean>>({});
  const [documentType, setDocumentType] = useState<Record<string, boolean>>({});
  const [documentCategory, setDocumentCategory] = useState<
    Record<string, boolean>
  >({});
  const [documentSubCategory, setDocumentSubCategory] = useState<
    Record<string, boolean>
  >({});
  const [publishedSince, setPublishedSince] = useState<Date | null>(null);

  // Dropdown expanded states
  const [expandedDropdowns, setExpandedDropdowns] = useState<
    Record<string, boolean>
  >({
    policyCode: false,
    language: false,
    documentType: false,
    documentCategory: false,
    documentSubCategory: false,
  });

  // Update filter states when filterValues change
  useEffect(() => {
    if (filterValues) {
      setPolicyCode(createFilterState(filterValues.policyCode));
      setLanguage(createFilterState(filterValues.documentLanguage));
      setDocumentType(createFilterState(filterValues.documentType));
      setDocumentCategory(createFilterState(filterValues.documentCategory));
      setDocumentSubCategory(
        createFilterState(filterValues.documentSubCategory)
      );
    }
  }, [filterValues]);

  // Toggle dropdown expansion
  const toggleDropdown = (dropdownKey: string) => {
    setExpandedDropdowns((prev) => ({
      ...prev,
      [dropdownKey]: !prev[dropdownKey],
    }));
  };

  const handleLanguageChange = (key: string) => {
    const newLanguage = {
      ...language,
      [key]: !language[key],
    };
    setLanguage(newLanguage);
    onLanguageChange?.(newLanguage);
  };

  const handlePolicyCodeChange = (key: string) => {
    const newPolicyCode = {
      ...policyCode,
      [key]: !policyCode[key],
    };
    setPolicyCode(newPolicyCode);
    onPolicyCodeChange?.(newPolicyCode);
  };

  const handleClearAll = () => {
    const emptyOrFromValues = (options?: Array<Record<string, number>>) =>
      options ? createFilterState(options) : {};

    const newLanguage = emptyOrFromValues(filterValues?.documentLanguage);
    const newPolicyCode = emptyOrFromValues(filterValues?.policyCode);
    const newDocumentType = emptyOrFromValues(filterValues?.documentType);
    const newDocumentCategory = emptyOrFromValues(
      filterValues?.documentCategory
    );
    const newDocumentSubCategory = emptyOrFromValues(
      filterValues?.documentSubCategory
    );

    setLanguage(newLanguage);
    setPolicyCode(newPolicyCode);
    setDocumentType(newDocumentType);
    setDocumentCategory(newDocumentCategory);
    setDocumentSubCategory(newDocumentSubCategory);
    setPublishedSince(null);

    onLanguageChange?.(newLanguage);
    onPolicyCodeChange?.(newPolicyCode);
    onDocumentTypeChange?.(newDocumentType);
    onDocumentCategoryChange?.(newDocumentCategory);
    onDocumentSubCategoryChange?.(newDocumentSubCategory);
    onPublishedSinceChange?.(null);
  };

  const handleDocumentTypeChange = (key: string) => {
    const newDocumentType = {
      ...documentType,
      [key]: !documentType[key],
    };
    setDocumentType(newDocumentType);
    onDocumentTypeChange?.(newDocumentType);
  };

  const handleDocumentCategoryChange = (key: string) => {
    const newDocumentCategory = {
      ...documentCategory,
      [key]: !documentCategory[key],
    };
    setDocumentCategory(newDocumentCategory);
    onDocumentCategoryChange?.(newDocumentCategory);
  };

  const handleDocumentSubCategoryChange = (key: string) => {
    const newDocumentSubCategory = {
      ...documentSubCategory,
      [key]: !documentSubCategory[key],
    };
    setDocumentSubCategory(newDocumentSubCategory);
    onDocumentSubCategoryChange?.(newDocumentSubCategory);
  };

  const hasFilterData = React.useMemo(() => {
    if (!filterValues) return false;
    const {
      policyCode: fvPolicyCode,
      documentLanguage: fvLanguage,
      documentType: fvDocType,
      documentCategory: fvDocCategory,
      documentSubCategory: fvDocSubCategory,
    } = filterValues;
    return [
      fvPolicyCode,
      fvLanguage,
      fvDocType,
      fvDocCategory,
      fvDocSubCategory,
    ].some((arr) => Array.isArray(arr) && arr.length > 0);
  }, [filterValues]);

  // Helper function to render dropdown filter sections
  const renderDropdownFilter = (
    title: string,
    filterState: Record<string, boolean>,
    onItemChange: (key: string) => void,
    sectionId: string
  ) => {
    if (Object.keys(filterState).length === 0) return null;

    const isExpanded = expandedDropdowns[sectionId];

    return (
      <Box sx={{ mb: 2 }}>
        {/* Dropdown Header */}
        <Box
          onClick={() => toggleDropdown(sectionId)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            padding: theme.spacing(1.5, 0),
            borderBottom: isExpanded ? "none" : `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
          <IconButton size="small" sx={{ p: 0 }}>
            {isExpanded ? <ExpandUpIcon /> : <ExpandDownIcon />}
          </IconButton>
        </Box>

        {/* Dropdown Content */}
        <Collapse in={isExpanded}>
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: "background.paper",
            }}
          >
            {Object.keys(filterState).map((key) => {
              const displayLabel = key;
              return (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1.5,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={() => onItemChange(key)}
                >
                  <Checkbox
                    checked={filterState[key]}
                    onChange={() => onItemChange(key)}
                    size="small"
                    sx={{ mr: 2, p: 0 }}
                    id={`${sectionId}-${key}`}
                    name={`${sectionId}-${key}`}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      flexGrow: 1,
                    }}
                  >
                    {displayLabel}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box
      role="complementary"
      aria-label="Filter panel"
      sx={{
        width: {
          lg: theme.spacing(35), // 280px equivalent at default 8px spacing
          xl: theme.spacing(40), // 320px for larger screens
        },
        flexShrink: 0,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          pt: 3,
          pr: 0,
          pb: 3,
          pl: 3,
          position: "sticky",
          top: theme.spacing(2.5), // 20px equivalent
          maxHeight: `calc(100vh - ${theme.spacing(2.5)})`,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.5 }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 400,
            }}
          >
            Filter
          </Typography>
          <FilterIcon size="lg" />
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {/* Clear All (render only when filter data is available) */}
        {!isLoading && hasFilterData && (
          <Box sx={{ mb: 1, display: "flex" }}>
            <Button
              onClick={handleClearAll}
              variant="outlined"
              size="small"
              sx={{ width: "100%" }}
            >
              <Box
                sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
              >
                Clear all <ClearIcon size="md" />
              </Box>
            </Button>
          </Box>
        )}

        {/* Dynamic Filter Dropdowns in required order */}
        {renderDropdownFilter(
          "Language",
          language,
          handleLanguageChange,
          "language"
        )}

        {renderDropdownFilter(
          "Policy Code",
          policyCode,
          handlePolicyCodeChange,
          "policyCode"
        )}

        {renderDropdownFilter(
          "Document Type",
          documentType,
          handleDocumentTypeChange,
          "documentType"
        )}

        {renderDropdownFilter(
          "Document Category",
          documentCategory,
          handleDocumentCategoryChange,
          "documentCategory"
        )}

        {renderDropdownFilter(
          "Document Sub Category",
          documentSubCategory,
          handleDocumentSubCategoryChange,
          "documentSubCategory"
        )}

        {/* Published Since Date Picker (bottom) */}
        <Box sx={{ mt: 2 }}>
          {isLoading || !filterValues ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 40,
              }}
            >
              <CircularProgress size={20} />
            </Box>
          ) : (
            <DesktopDatePicker
              label="Published since"
              value={publishedSince}
              onChange={(newValue) => {
                const asDate = newValue
                  ? newValue instanceof Date
                    ? newValue
                    : typeof (newValue as any).toDate === "function"
                    ? (newValue as any).toDate()
                    : new Date(newValue as any)
                  : null;
                setPublishedSince(asDate);
                onPublishedSinceChange?.(asDate);
              }}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default FilterPanel;
